import type { UserNotifier } from 'hooks/useUserNotification';
import { buildUserNotification } from 'hooks/useUserNotification';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import { connect } from 'react-redux';
import { getCurrentOptimizeId, getErrors } from 'state/advisory/basket-optimizer/selectors';
import { BatchFormInfos } from 'state/batchForm/infos/infos.model';
import {
  getBlotterPanelItem,
  getSecondaryOrderBlotterPanelSelected,
  getSecondaryTradeBlotterPanelSelected,
} from 'state/blotters/blotterPanel/blotterPanel.selector';
import { getOrderById } from 'state/blotters/secondaryMarketOrderBlotter/secondaryMarketOrderBlotter.selector';
import { getSecondaryTradeBlotterItemByQuoteId } from 'state/blotters/secondaryMarketTradeBlotter/secondaryMarketTradeBlotter.selector';
import { selectTradesBlottersType } from 'state/blotters/trades/tradesBlotters.selector';
import { selectEssentialFormInfos } from 'state/essential/infos/selectors';
import {
  getOrderId as getDlpOrderID,
  getOrderRejections as getDlpOrderRejections,
} from 'state/secondaryMarket/dlpOrder/order/order.selector';
import type { QuoteState } from 'state/secondaryMarket/live/quote/liveQuote.model';
import { getFocusedProduct } from 'state/secondaryMarket/productSearch/productSearch.selectors';
import type { SecondaryMarketQuoteRtForm } from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm';
import {
  addonSelector,
  getBatchFormInfos,
  getBatchTabContent,
  getCurrentRoute,
  getCurrentTabIdFromRouter,
  getDynamicFormInfos,
  getFormQuoteDetails,
  getFormQuoteRejections,
  getFormQuoteResult,
  getOrdersBasketWorkflowErrors,
  getStaticFormInfos,
  getUserPreferencesNotifications,
  getWorkspaceTypeFromRouter,
  priceImprovementBlotter,
  productModificationSelectors,
  tradeAllocationSelectors,
} from 'state/selectors';
import {
  getSecondaryMarketFileCode,
  getSecondaryMarketIsinCode,
  getSecondaryMarketOrderDetails,
  getSecondaryMarketProductKey,
  getSecondaryMarketQuoteDetails,
  getSecondaryMarketQuoteRejections,
  getSecondaryMarketTradeRejections,
} from 'state/selectors/common/secondaryMarketForm';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType, TabType, WorkspaceType } from 'state/workspaces/model';
import { SecondaryMarketPostEventState } from 'state/workspaces/secondary-market/post-event/secondaryMarketPostEvent.model';
import { getTabType } from 'state/workspaces/tabs/tabs.selector';

export interface LifeRingModalStateProps {
  externalQuoteId: string | undefined;
  quoteId: string | undefined;
  orderId: string | undefined;
  pricerId: string | undefined;
  workflowErrors: readonly NextWorkflowError[] | undefined;
  workspaceType?: WorkspaceType;
  isinCode?: string;
  fileCode?: string;
  productId?: string;
  tradeId?: string;
  dealId?: string;
  productKey?: string;
  currentRoute?: string;
  currentTabId?: string;
  currentTabType?: TabType;
  contactIds?: string[];

  batchColumns?: {
    quoteId?: string;
    negotiationId?: string;
    pricerId?: string;
    rejectionMessage?: string;
  }[];
  negotiationId?: string;
  basketOptimizerId?: string;
  basketOptimizerError?: string;
}

function getTradeAllocationErrors(state: AppState, currentTabId: string): NextWorkflowError[] {
  const error = tradeAllocationSelectors.getApplyError(state, currentTabId);
  if (!error) {
    return [];
  }
  return [{ rejectionMessage: error.message }];
}

function getFileCode(state: AppState, currentTabType: TabType, currentTabId: string): string | undefined {
  if (currentTabType === 'addOn') {
    return addonSelector.getFileCode(state, currentTabId);
  }
  if (currentTabType === 'tradeAllocation') {
    return tradeAllocationSelectors.getFileCode(state, currentTabId);
  }
  return undefined;
}

function getIsin(state: AppState, currentTabType: TabType, currentTabId: string): string | undefined {
  if (currentTabType === 'addOn') {
    return addonSelector.getIsinCode(state, currentTabId);
  }
  if (currentTabType === 'tradeAllocation') {
    return tradeAllocationSelectors.getIsin(state, currentTabId);
  }
  return undefined;
}

function getProductId(state: AppState, currentTabType: TabType, currentTabId: string): string | undefined {
  if (currentTabType === 'addOn') {
    return addonSelector.getProductId(state, currentTabId);
  }
  if (currentTabType === 'productModification') {
    return productModificationSelectors.getProductId(state, currentTabId);
  }
  return undefined;
}

function getTradeId(state: AppState, currentTabType: TabType, currentTabId: string): string | undefined {
  if (currentTabType === 'tradeAllocation') {
    return tradeAllocationSelectors.getTradeId(state, currentTabId);
  }
  return undefined;
}

function getFormInfos(state: AppState, workspace: QuoteWorkspaceType, tabId: string) {
  switch (workspace) {
    case 'static':
      return getStaticFormInfos(state, tabId);
    case 'dynamic':
      return getDynamicFormInfos(state, tabId);
    case 'postTrade':
      return getDynamicFormInfos(state, tabId);
    case 'essential':
      return selectEssentialFormInfos(state, tabId);
    default:
      return undefined;
  }
}

export function getNegotiationId(state: AppState, workspace: QuoteWorkspaceType, tabId: string): string | undefined {
  const formInfo = getFormInfos(state, workspace, tabId);
  return formInfo?.negotiationId;
}

export function getBatchScreenColumnsIds(
  state: AppState,
  tabId: string,
): { quoteId?: string; negotiationId?: string }[] | undefined {
  return getBatchScreenFormInfos(state, tabId)
    ?.filter(f => f?.negotiationId !== undefined)
    ?.map(f => ({ quoteId: f?.quoteId, negotiationId: f?.negotiationId }));
}

export function getBatchScreenQuoteIds(state: AppState, tabId: string): string[] | undefined {
  return getBatchScreenFormInfos(state, tabId)
    ?.map(formInfo => formInfo?.quoteId)
    .filter(s => s !== undefined);
}

export function getBatchScreenFormInfos(state: AppState, tabId: string): (BatchFormInfos | undefined)[] {
  const batchContent = getBatchTabContent(state, tabId);
  const formIds = batchContent?.forms.map(form => form.negotiationId);
  if (!Array.isArray(formIds)) {
    return [];
  }
  return formIds.map(formId => {
    const formInfos = getBatchFormInfos(state, formId);
    return formInfos;
  });
}

export function lifeRingModalMapStateToProps(state: AppState): LifeRingModalStateProps {
  const currentRoute = getCurrentRoute(state);
  const workspaceType = getWorkspaceTypeFromRouter(state);
  const currentTabId = getCurrentTabIdFromRouter(state);
  const currentTabType = getTabType(state, currentTabId);
  const userPrefsNotifications = getUserPreferencesNotifications(state);
  const contactIds = userPrefsNotifications?.contactIds;

  const defaults = {
    externalQuoteId: undefined,
    quoteId: undefined,
    orderId: undefined,
    pricerId: undefined,
    workflowErrors: undefined,
    workspaceType,
    isinCode: undefined,
    productKey: undefined,
    currentRoute,
    currentTabId,
    currentTabType,
    contactIds,
  };

  if (currentRoute === '/quote-blotter') {
    const blotterItem = getBlotterPanelItem(state);
    return {
      ...defaults,
      externalQuoteId: blotterItem?.externalQuoteId,
      quoteId: blotterItem?.quoteId,
      pricerId: blotterItem?.pricerRfqId,
    };
  } else if (currentRoute === '/secondary-orders-blotter') {
    const orderId = getSecondaryOrderBlotterPanelSelected(state);
    const item = getOrderById(state, orderId);
    return { ...defaults, orderId, isinCode: item?.isin, fileCode: item?.fileCode, dealId: item?.dealId };
  } else if (currentRoute === '/secondary-trade-blotter') {
    const quoteId = getSecondaryTradeBlotterPanelSelected(state);
    const item = getSecondaryTradeBlotterItemByQuoteId(state, quoteId);
    return { ...defaults, quoteId, isinCode: item?.isin, fileCode: item?.fileCode };
  } else if (currentRoute === '/trade-blotter') {
    const type = selectTradesBlottersType(state);
    const blotterItem = getBlotterPanelItem(state);

    switch (type) {
      case 'products':
        return {
          ...defaults,
          isinCode: blotterItem?.isin,
          fileCode: blotterItem?.fileCode,
        };
      case 'trades':
        return {
          ...defaults,
          isinCode: blotterItem?.isin,
          fileCode: blotterItem?.fileCode,
          quoteId: blotterItem?.quoteId,
          externalQuoteId: blotterItem?.externalQuoteId,
          tradeId: blotterItem?.tradeId,
        };
      case 'dlpProducts':
        if (blotterItem?.blotterItemType === 'DlpProduct') {
          return {
            ...defaults,
            pricerId: blotterItem?.pricerRfqId,
            isinCode: blotterItem?.isin,
          };
        }
        if (blotterItem?.blotterItemType === 'DlpProductTrade') {
          return {
            ...defaults,
            isinCode: blotterItem?.productIsin,
            tradeId: blotterItem?.tradeReference,
          };
        }
    }
  } else if (currentRoute === '/price-improvement-blotter') {
    const item = priceImprovementBlotter.getDetailItem(state)!;
    return {
      ...defaults,
      externalQuoteId: item?.quoteId,
      pricerId: item?.pricerId,
    };
  }

  if (currentTabId === undefined) {
    if (workspaceType === 'secondaryMarketQuotes') {
      const product = getFocusedProduct(state);
      return {
        quoteId: undefined,
        orderId: undefined,
        workflowErrors: undefined,
        externalQuoteId: undefined,
        pricerId: undefined,
        workspaceType,
        isinCode: product?.isin,
        fileCode: product?.fileCode,
        productKey: product?.searchKey,
        currentRoute,
        currentTabId,
        currentTabType,
        contactIds,
      };
    }
    return defaults;
  }

  if (
    workspaceType === 'static' ||
    workspaceType === 'dynamic' ||
    workspaceType === 'postTrade' ||
    workspaceType === 'essential'
  ) {
    const quoteResult = getFormQuoteResult(state, workspaceType, currentTabId);
    const quoteRejections = getFormQuoteRejections(state, workspaceType, currentTabId) ?? [];
    const productModificationErrors = productModificationSelectors.getFormQuoteRejections(state, currentTabId) ?? [];
    const tradeAllocationErrors = getTradeAllocationErrors(state, currentTabId);
    const workflowErrors = [...quoteRejections, ...productModificationErrors, ...tradeAllocationErrors];
    return {
      basketOptimizerId: getCurrentOptimizeId(state, currentTabId),
      basketOptimizerError: getErrors(state, currentTabId),
      negotiationId: getNegotiationId(state, workspaceType, currentTabId),
      externalQuoteId: quoteResult?.quoteExternalId,
      quoteId: quoteResult?.quoteId,
      orderId: undefined,
      pricerId: getFormQuoteDetails(state, workspaceType, currentTabId)?.PricerRfqId,
      workflowErrors,
      workspaceType,
      currentRoute,
      currentTabId,
      currentTabType,
      contactIds,
      fileCode: getFileCode(state, currentTabType, currentTabId),
      isinCode: getIsin(state, currentTabType, currentTabId),
      productId: getProductId(state, currentTabType, currentTabId),
      tradeId: getTradeId(state, currentTabType, currentTabId),
    };
  }

  if (workspaceType === 'secondaryMarketQuotes' || workspaceType === 'secondaryMarketPostEvents') {
    const tabType = getTabType(state, currentTabId);
    const quoteDetails = getSecondaryMarketQuoteDetails(state, currentTabId);
    const orderDetails = getSecondaryMarketOrderDetails(state, currentTabId);
    const dlpOrderId = getDlpOrderID(state, currentTabId);

    const isinCode = getSecondaryMarketIsinCode(state, currentTabId);
    const fileCode = getSecondaryMarketFileCode(state, currentTabId);
    const productKey = getSecondaryMarketProductKey(state, currentTabId);

    const quoteRejections = getSecondaryMarketQuoteRejections(state, currentTabId);
    const tradeRejections = getSecondaryMarketTradeRejections(state, currentTabId);
    const dlpOrderRejections = getDlpOrderRejections(state, currentTabId) ?? [];
    const rejections = [...quoteRejections, ...tradeRejections, ...dlpOrderRejections];

    const quoteId = (quoteDetails as SecondaryMarketQuoteRtForm | QuoteState)?.quoteId;
    const orderId =
      tabType === 'dlpProducts'
        ? dlpOrderId
        : (orderDetails?.id ?? (quoteDetails as SecondaryMarketPostEventState)?.orderId);

    return {
      externalQuoteId: undefined,
      quoteId: quoteId === orderId ? undefined : (quoteId ?? undefined),
      orderId,
      pricerId: undefined,
      workflowErrors: rejections,
      workspaceType,
      isinCode,
      fileCode,
      productKey,
      currentRoute,
      currentTabId,
      currentTabType,
      contactIds,
    };
  }

  if (workspaceType === 'ordersBasket') {
    const ordersBasketWorkflowRejections = getOrdersBasketWorkflowErrors(state, currentTabId);
    return {
      externalQuoteId: undefined,
      quoteId: undefined,
      orderId: undefined,
      pricerId: undefined,
      workflowErrors: ordersBasketWorkflowRejections,
      workspaceType,
      currentRoute,
      currentTabId,
      currentTabType,
      contactIds,
    };
  }

  if (workspaceType === 'newBatch') {
    const batchContent = getBatchTabContent(state, currentTabId);
    const batchColumns = batchContent?.formIds.map(formId => {
      const batchFormInfos = getBatchFormInfos(state, formId);
      const quoteDetails = getFormQuoteResult(state, 'newBatch', formId);

      return {
        quoteId: quoteDetails?.quoteExternalId,
        pricerRfqId: quoteDetails?.pricerRfqId,
        negotiationId: batchFormInfos?.negotiationId,
        rejectionMessage: quoteDetails?.rejections?.map(r => r.rejectionMessage).join('; '),
      };
    });
    return {
      ...defaults,
      batchColumns,
    };
  }

  return {
    externalQuoteId: undefined,
    quoteId: undefined,
    orderId: undefined,
    pricerId: undefined,
    workflowErrors: undefined,
    workspaceType: undefined,
    currentRoute: undefined,
    currentTabId: undefined,
    currentTabType: undefined,
    contactIds: undefined,
  };
}

const mapDispatchToProps = (dispatch: ThunkDispatch): UserNotifier => buildUserNotification(dispatch);

export const connectLifeRingModalComponent = connect(lifeRingModalMapStateToProps, mapDispatchToProps);
